import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useRaiseInvoiceMutation } from '@/api/endpoints';
import type { Invoice } from '@/api/types';
import { Button } from '@/design/Button';
import { Field, TextInput } from '@/design/Field';
import { Modal } from '@/design/Modal';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import { checkPan } from '@/lib/validate';

/**
 * Raising an invoice, and asking for the customer's PAN while somebody is
 * looking at both the customer and the paper.
 *
 * It is one optional field, so it could have been a silent default — raise,
 * and add the number later. That is worse than it sounds: an issued invoice is
 * never edited here, so "later" means voiding it and raising another. The
 * question has to be asked before the number is allocated, not after.
 *
 * Skippable in one press, because most bills do not carry a PAN. A walk-in
 * tourist has no Nepali tax number and does not need one.
 */

interface Props {
  readonly booking: { readonly id: string; readonly reference: string } | null;
  /** Whatever is already on the customer's record, so it is typed once ever. */
  readonly customerPan: string;
  readonly onRaised: (invoice: Invoice) => void;
  readonly onClose: () => void;
}

export function RaiseInvoiceModal({
  booking,
  customerPan,
  onRaised,
  onClose,
}: Props): ReactElement {
  const [pan, setPan] = useState(customerPan);
  const [raise, raising] = useRaiseInvoiceMutation();
  const toast = useToast();
  const problems = readApiError(raising.error);

  // Re-seeded when a different booking is opened, so one customer's number is
  // never sitting in the box waiting to be printed on another's invoice.
  useEffect(() => setPan(customerPan), [booking?.id, customerPan]);

  const panProblem = pan.trim() === '' ? undefined : checkPan(pan);

  async function save(): Promise<void> {
    if (booking === null) return;
    try {
      const invoice = await raise({ booking: booking.id, customer_pan: pan.trim() }).unwrap();
      toast.done(`${invoice.number} raised.`);
      onRaised(invoice);
    } catch {
      // The refusal — an already-invoiced booking, a no-bill sale — renders
      // in the modal, which is where the press happened.
    }
  }

  return (
    <Modal
      open={booking !== null}
      onClose={onClose}
      title={booking === null ? 'Raise an invoice' : `Invoice for ${booking.reference}`}
      note="The number is allocated when this is raised, and an issued invoice is never edited."
      footer={
        <>
          <Button
            intent="primary"
            size="touch"
            type="submit"
            form="raise-invoice"
            disabled={raising.isLoading || panProblem !== undefined}
          >
            {raising.isLoading ? 'Raising' : 'Raise the invoice'}
          </Button>
          <Button size="touch" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form
        id="raise-invoice"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field
          label="Customer PAN or VAT number"
          error={panProblem ?? problems.fields['customer_pan']}
        >
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              identifier
              value={pan}
              onChange={setPan}
            />
          )}
        </Field>

        <p className="text-small text-ink-muted">
          Leave it empty if they have not given one — most bills do not carry a PAN. A number
          entered here is kept on the customer, so the next invoice already has it.
        </p>

        {problems.detail !== undefined && (
          <p
            className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
            role="alert"
          >
            {problems.detail}
          </p>
        )}
      </form>
    </Modal>
  );
}
